/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

/**
 * Gets the resolved horizontal scrollbar layout.
 * @param min The range min.
 * @param max The range max.
 * @param globalMin The global min.
 * @param globalMax The global max.
 * @param isRTL Whether the reading direction is rtl.
 * @returns The resolved horizontal scrollbar layout.
 */
const getResolvedHorizontalScrollbarLayout = (
  min: number,
  max: number,
  globalMin: number,
  globalMax: number,
  isRTL: boolean
) => {
  return (width: number) => {
    const localRange = max - min;
    const globalRange = globalMax - globalMin;
    const thumbWidth = (width * localRange) / globalRange;
    const thumbStartOffset = (width * (min - globalMin)) / globalRange;
    const logicalThumbLeft = isRTL ? width - thumbStartOffset - thumbWidth : thumbStartOffset;
    const thumbLeft = isRTL ? undefined : thumbStartOffset;
    const thumbRight = isRTL ? thumbStartOffset : undefined;

    const draggableRegion = {
      xMin: logicalThumbLeft,
      xMax: logicalThumbLeft + thumbWidth,
      yMin: -Infinity,
      yMax: Infinity
    };

    const getNewRange = (newThumbLeft: number) => {
      const p = isRTL ? Math.max(0, width - newThumbLeft - thumbWidth) : newThumbLeft;
      const newMin = Math.min(globalMin + (p / width) * globalRange, globalMax - localRange);
      const newMax = newMin + localRange;
      return { newMin, newMax };
    };

    return {
      thumbWidth,
      thumbLeft,
      thumbRight,
      draggableRegion,
      getNewRange
    };
  };
};

/**
 * Gets the resolved vertical scrollbar layout.
 * @param min The range min.
 * @param max The range max (if any).
 * @param globalMin The global min.
 * @param globalMax The global max.
 * @returns The resolved vertical scrollbar layout.
 */
const getResolvedVerticalScrollbarLayout = (
  min: number,
  max: number | undefined,
  globalMin: number,
  globalMax: number
) => {
  return (height: number) => {
    // It's possible for globalRange to be smaller than localRange
    // due to the component being taller than the height the data occupies
    // e.g. tall Gantt with 1 row
    // So throughout this code we make sure to clamp thumb dimensions and ranges
    const localRange = (max ?? height + min) - min;
    const globalRange = globalMax - globalMin;
    const thumbHeight = Math.min(height, (height * localRange) / globalRange);
    const thumbTop = Math.max(0, (height * (min - globalMin)) / globalRange);

    const draggableRegion = {
      xMin: -Infinity,
      xMax: Infinity,
      yMin: thumbTop,
      yMax: thumbTop + thumbHeight
    };

    const getNewRange = (newThumbTop: number) => {
      const newMin = Math.max(
        0,
        Math.min(globalMin + (newThumbTop / height) * globalRange, globalMax - localRange)
      );
      const newMax = newMin + localRange;
      return { newMin, newMax };
    };

    return {
      thumbHeight,
      thumbTop,
      draggableRegion,
      getNewRange
    };
  };
};

export { getResolvedHorizontalScrollbarLayout, getResolvedVerticalScrollbarLayout };
